/**
 * Typed TanStack hooks for the correspondence API. Two surfaces:
 *
 *   - `useCorrespondenceQuery(action, input, options?)` — idempotent reads
 *     (cached, refetched, revalidated via TanStack Query).
 *   - `useCorrespondenceMutation(action, options?)` — side-effectful writes
 *     (single-fire, manual invalidation).
 *
 * Per-action request/response types come from the `RequestByAction` /
 * `ResponseByAction` lookup maps. The map shape is intentional — distributive
 * `Extract` over the 25-arm action union didn't scale and made TS inference
 * unworkably slow.
 *
 * See `.claude/rules/correspondence-api-transition.md` for the surrounding
 * rules.
 */

import { FunctionRegion } from '@supabase/supabase-js';
import {
  useMutation,
  useQuery,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { supabase } from '@/integrations/supabase/client';
import {
  getSupabaseFunctionErrorStructured,
  type StructuredFunctionError,
} from '@/lib/supabase-function-error-structured';
import {
  CorrespondenceApiError,
  UnsupportedOnProviderError,
} from '@/lib/errors/correspondence-api-error';

import { buildBody } from './use-correspondence-api.body-whitelist';
import {
  queryKeyForAction,
  type QueryKeyInputShape,
  type QueryKeyProviderHint,
} from './use-correspondence-api.query-keys';

import type {
  CorrespondenceAction,
  Provider,
  ReadAction,
  RequestByAction,
  ResponseByAction,
  WriteAction,
} from '@/lib/correspondence/contracts';

// Re-exports for sites that import query-key helpers directly from the
// hooks module. Keeps the public-API surface in one place.
export {
  queryKeyForAction,
  type QueryKeyInputShape,
  type QueryKeyProviderHint,
} from './use-correspondence-api.query-keys';

const FACADE_FUNCTION_NAME = 'correspondence-api';
const MAX_RETRIES = 3;

/**
 * 2xx response envelope: every successful call returns `{data, provider}`.
 * Both hooks strip the envelope and return `data` to callers.
 */
interface SuccessEnvelope<T> {
  data: T;
  provider: Provider | 'mixed';
}

// ---------------------------------------------------------------------------
// Retry classifier — exported as a pure function for isolated testing.
// ---------------------------------------------------------------------------

/**
 * Decides whether a failed call should be retried.
 *
 * Retry taxonomy:
 *   - Network / 5xx → retry up to `MAX_RETRIES` (TanStack default backoff).
 *   - 408 (Request Timeout) → retry at most ONCE.
 *   - 429 (Too Many Requests) → retry with default backoff. `Retry-After`
 *     is intentionally not parsed; TanStack doesn't expose per-attempt
 *     delays here, and the default exponential backoff has been adequate.
 *   - Other 4xx (incl. `UNSUPPORTED_ON_PROVIDER`) → NO retry. These are
 *     structural rejections; retrying just burns quota.
 *
 * Exported as a pure function so tests can assert the taxonomy without
 * standing up a full `QueryClient`.
 */
export function shouldRetryCorrespondenceError(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) {
    return false;
  }
  if (error instanceof CorrespondenceApiError) {
    const { httpStatus } = error;
    if (httpStatus === 0 || httpStatus >= 500) {
      return true;
    }
    if (httpStatus === 408) {
      return failureCount < 1;
    }
    if (httpStatus === 429) {
      return true;
    }
    return false;
  }
  // Non-typed errors are treated as transport failures (network drop,
  // AbortController, Supabase SDK runtime issues) — retry.
  return true;
}

// ---------------------------------------------------------------------------
// Core invoke — shared by useQuery + useMutation.
// ---------------------------------------------------------------------------

/**
 * Low-level invoke with structured error parsing. The two hooks above
 * thread this through their queryFn/mutationFn so error-shape translation
 * lives in exactly one place.
 *
 * Direct callers are limited to: capability-map prefetch, and imperative
 * one-shot calls that cannot flow through `useCorrespondenceQuery` /
 * `useCorrespondenceMutation` (see correspondence-api-transition.md).
 *
 * MUST NOT be used in place of `useCorrespondenceQuery` for single-page
 * reads — the hook adds retry classification, typed-error-aware Sentry
 * tagging, and query-key invalidation that a bare invoke does not.
 *
 * `opts.signal` aborts the underlying fetch (bulk fan-outs cancel their
 * in-flight probes with it); an aborted call rejects with the SDK's abort
 * error, which `isTransientFetchError` classifies as transient.
 */
export async function invokeCorrespondenceApi<A extends CorrespondenceAction>(
  action: A,
  input: RequestByAction[A],
  opts: { signal?: AbortSignal } = {},
): Promise<ResponseByAction[A]> {
  const body = buildBody(action, input);
  const response = await supabase.functions.invoke<SuccessEnvelope<ResponseByAction[A]>>(
    FACADE_FUNCTION_NAME,
    { body, signal: opts.signal, region: FunctionRegion.ApSoutheast2 },
  );
  // Supabase SDK's FunctionsResponse generic types `data` as `T | null`
  // but the declaration file widens `error` loosely. Read through an
  // explicit cast rather than destructuring the `any`-typed value.
  const typed = response as {
    data: SuccessEnvelope<ResponseByAction[A]> | null;
    error: unknown;
  };
  const { data } = typed;
  const { error } = typed;

  if (error) {
    const structured = await getSupabaseFunctionErrorStructured(error);
    throw toTypedError(structured);
  }

  if (!data || typeof data !== 'object') {
    // Shouldn't happen on a 2xx response, but the narrowing protects
    // downstream consumers from null derefs.
    throw new CorrespondenceApiError({
      message: 'correspondence-api returned an empty response',
      code: 'INTERNAL_ERROR',
      httpStatus: 0,
      action,
    });
  }

  return data.data;
}

function toTypedError(structured: StructuredFunctionError): CorrespondenceApiError {
  if (
    structured.code === 'UNSUPPORTED_ON_PROVIDER' &&
    structured.provider !== undefined &&
    structured.action !== undefined
  ) {
    return new UnsupportedOnProviderError(
      structured.provider,
      structured.action,
      structured.details,
    );
  }
  return new CorrespondenceApiError({
    message: structured.message,
    code: structured.code,
    httpStatus: structured.httpStatus,
    provider: structured.provider,
    action: structured.action,
    details: structured.details,
  });
}

// ---------------------------------------------------------------------------
// Provider-hint dev-warn
// ---------------------------------------------------------------------------

/**
 * Emits a dev-mode `console.warn` when a provider-scoped action is called
 * without either a `providerHint` or an explicit `queryKey` override. The
 * default `'correspondence'` query-key prefix bypasses the legacy
 * `['gmail', …]` / `['outlook', …]` invalidation paths, so callers that
 * still rely on legacy cache shape need to opt in explicitly.
 *
 * One-shot per action literal across the module's lifetime — repeat
 * mounts do not re-fire the warning.
 */
const warnedActions = new Set<ReadAction>();

function warnOnMissingProviderHint(
  action: ReadAction,
  queryKeyOverride: QueryKey | undefined,
  providerHint: QueryKeyProviderHint | undefined,
): void {
  if (!import.meta.env.DEV) {
    return;
  }
  if (action === 'get_capabilities') {
    return;
  }
  if (queryKeyOverride !== undefined || providerHint !== undefined) {
    return;
  }
  if (warnedActions.has(action)) {
    return;
  }
  warnedActions.add(action);
  console.warn(
    `[correspondence-api] useCorrespondenceQuery('${action}', …) ` +
      "uses the default 'correspondence' query-key prefix. Pass " +
      "providerHint: 'gmail' | 'outlook' in options to match legacy " +
      'invalidation paths. See .claude/rules/correspondence-api-transition.md rule #24.',
  );
}

// ---------------------------------------------------------------------------
// useCorrespondenceQuery
// ---------------------------------------------------------------------------

/**
 * Option overrides accepted by `useCorrespondenceQuery`. Mirrors TanStack's
 * `UseQueryOptions` but drops `queryFn` (owned by this hook) and narrows
 * `queryKey` so callers can pin a legacy-shaped key (pilot migration
 * use-case — see plan §Query-key map).
 */
export type CorrespondenceQueryOptions<A extends ReadAction> = Omit<
  UseQueryOptions<ResponseByAction[A], CorrespondenceApiError>,
  'queryFn' | 'queryKey'
> & {
  queryKey?: QueryKey;
  /** Override the inferred provider hint for default query-key shape. */
  providerHint?: QueryKeyProviderHint;
};

export function useCorrespondenceQuery<A extends ReadAction>(
  action: A,
  input: RequestByAction[A],
  options: CorrespondenceQueryOptions<A> = {},
): UseQueryResult<ResponseByAction[A], CorrespondenceApiError> {
  const { queryKey: queryKeyOverride, providerHint, ...rest } = options;
  warnOnMissingProviderHint(action, queryKeyOverride, providerHint);

  // The query key only reads `QueryKeyInputShape` fields (messageId, threadId,
  // organizationId, accountId). Depending on primitives rather than
  // `JSON.stringify(input)` keeps memo deps O(1) regardless of input size —
  // critical when inputs might carry large payloads (save_draft bodyHtml,
  // attachments, etc.) on a future non-pilot migration.
  const keyInput = input as QueryKeyInputShape;
  const queryKey = useMemo<QueryKey>(
    () =>
      queryKeyOverride ??
      queryKeyForAction(
        action,
        {
          messageId: keyInput.messageId,
          threadId: keyInput.threadId,
          organizationId: keyInput.organizationId,
          accountId: keyInput.accountId,
        },
        providerHint,
      ),
    // Bounded primitive deps — queryKeyForAction only reads these four
    // fields, so any other change in `input` cannot affect the key.
    [
      action,
      providerHint,
      queryKeyOverride,
      keyInput.messageId,
      keyInput.threadId,
      keyInput.organizationId,
      keyInput.accountId,
    ],
  );

  return useQuery<ResponseByAction[A], CorrespondenceApiError>({
    queryKey,
    queryFn: async ({ signal }) => await invokeCorrespondenceApi(action, input, { signal }),
    retry: shouldRetryCorrespondenceError,
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// useCorrespondenceMutation
// ---------------------------------------------------------------------------

export type CorrespondenceMutationOptions<A extends WriteAction> = Omit<
  UseMutationOptions<ResponseByAction[A], CorrespondenceApiError, RequestByAction[A]>,
  'mutationFn'
>;

export function useCorrespondenceMutation<A extends WriteAction>(
  action: A,
  options: CorrespondenceMutationOptions<A> = {},
): UseMutationResult<ResponseByAction[A], CorrespondenceApiError, RequestByAction[A]> {
  return useMutation<ResponseByAction[A], CorrespondenceApiError, RequestByAction[A]>({
    mutationFn: async (input) => await invokeCorrespondenceApi(action, input),
    retry: shouldRetryCorrespondenceError,
    ...options,
  });
}
